import React, { useState } from 'react';
import { Box, Typography, Chip, IconButton, Paper, Tabs, Tab, Tooltip, Select, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ResumenTab, EstudiosTab } from '../MedicalRecordPanel';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../hooks/useNotification';
import { updatePatient } from '../../services/api';
import { openWhatsApp } from './patientProfileHelpers';
import { EditPatientDialog } from './InfoTab';
import { AppointmentsTab } from './AppointmentsTab';
import { RecetasTab } from './RecetasTab';
import { OrdenesTab } from './OrdenesTab';
import { ComunicacionesTab } from './ComunicacionesTab';
import SelfUpdateLinkDialog from '../SelfUpdateLinkDialog';

// ── Sidebar izquierdo del paciente ────────────────────────────────────────────
export const PatientSidebar = ({ patient, onUpdated, sideTab, setSideTab, features, records, problems, activeProblem, onProblemClick, doctors, fullWidth = false }) => {
  const { isAdmin, isMedico, isRecepcionista } = useAuth();
  // El backend ya permite a médico editar pacientes (PUT /patients/{id}
  // usa get_admin_recepcionista_or_medico) — el botón "Editar" acá se
  // había quedado oculto solo para ese rol sin ninguna razón real,
  // reportado por el usuario 2026-10-12.
  const canEditInfo = isAdmin || isRecepcionista || isMedico;
  const canSeeClinicalInfo = isAdmin || isMedico;
  const [editOpen, setEditOpen] = useState(false);
  const [selfUpdateOpen, setSelfUpdateOpen] = useState(false);
  const notify = useNotification();
  // "Médico de cabecera" — pedido explícito del usuario: se completa solo
  // con el médico del primer turno del paciente (ver crud.create_appointment
  // en el backend) y queda fijo desde ahí, editable a mano en cualquier
  // momento. A diferencia del concepto de "último médico" de Patients.js
  // (que siempre sigue al turno más reciente), este es una asignación
  // estable — por eso vive en un Select propio, no en el form grande de
  // EditPatientDialog.
  const [savingAssignedDoctor, setSavingAssignedDoctor] = useState(false);
  const handleAssignedDoctorChange = async (e) => {
    const value = e.target.value;
    setSavingAssignedDoctor(true);
    try {
      await updatePatient(patient.id, { assigned_doctor_id: value || null });
      onUpdated();
    } catch (err) {
      notify(err.userMessage || 'No se pudo actualizar el médico de cabecera', 'error');
    } finally {
      setSavingAssignedDoctor(false);
    }
  };

  const age = patient.dob
    ? Math.floor((new Date() - new Date(patient.dob)) / (365.25 * 24 * 3600 * 1000))
    : null;
  const dobShort = patient.dob
    ? new Date(patient.dob).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  // Contar consultas vinculadas explícitamente a cada problema (via problem_id FK)
  const problemCounts = problems.reduce((acc, p) => {
    acc[p.id] = records.filter(r => r.problem_id === p.id).length;
    return acc;
  }, {});

  // Recepcionista no debe ver historia clínica (problemas, resumen, diagnósticos)
  const sideTabs = [
    ...(canSeeClinicalInfo ? ['Resumen'] : []),
    'Turnos',
    ...(canSeeClinicalInfo ? ['Estudios'] : []),
    ...(features.prescriptions && canSeeClinicalInfo ? ['Recetas'] : []),
    ...(features.study_orders && canSeeClinicalInfo ? ['Órdenes'] : []),
    ...(features.whatsapp ? ['Comunicaciones'] : []),
  ];

  return (
    <>
      <Paper elevation={0} sx={{
        width: { xs: '100%', md: fullWidth ? '100%' : '35%' },
        maxWidth: { md: fullWidth ? '100%' : 460 },
        flexShrink: 0,
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        alignSelf: 'flex-start',
        position: { md: 'sticky' }, top: { md: 16 },
      }}>

        {/* ── Cabecera del paciente ── */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, mb: 0.5, wordBreak: 'break-word' }}>
                {patient.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {age != null && (
                  <Typography variant="body2" color="text.secondary">
                    {age} años{dobShort && ` (${dobShort})`}
                  </Typography>
                )}
                {patient.gender && (
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {patient.gender}
                  </Typography>
                )}
                {patient.blood_type && (
                  <Chip label={patient.blood_type} size="small" color="error" variant="outlined"
                    sx={{ height: 20, fontSize: '0.72rem' }} />
                )}
              </Box>
            </Box>
            {canEditInfo && (
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {features?.patient_self_update && (
                  <Tooltip title="Generar link para que el paciente complete sus datos">
                    <IconButton size="small" onClick={() => setSelfUpdateOpen(true)}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Editar datos del paciente">
                  <IconButton size="small" onClick={() => setEditOpen(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Datos de contacto */}
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {patient.dni && (
              <Typography variant="body2" color="text.secondary">DNI: {patient.dni}</Typography>
            )}
            {patient.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{patient.phone}</Typography>
                <Tooltip title="Abrir en WhatsApp">
                  <IconButton
                    size="small" sx={{ p: 0.25, color: '#25D366' }}
                    onClick={() => openWhatsApp(patient.phone)}
                  >
                    <WhatsAppIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {patient.insurance_provider && (
              <Typography variant="body2" color="text.secondary">
                OS: {patient.insurance_provider}
                {patient.insurance_policy_number && ` · ${patient.insurance_policy_number}`}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary">Médico de cabecera:</Typography>
              {canEditInfo ? (
                <Select
                  value={patient.assigned_doctor_id || ''}
                  onChange={handleAssignedDoctorChange}
                  disabled={savingAssignedDoctor}
                  displayEmpty
                  variant="standard"
                  sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0 } }}
                >
                  <MenuItem value=""><em>Sin asignar</em></MenuItem>
                  {doctors.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </Select>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {doctors.find(d => d.id === patient.assigned_doctor_id)?.name || 'Sin asignar'}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Alergias */}
          {patient.allergies && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.25,
              bgcolor: 'warning.light', border: '1.5px solid #ff6d00', borderRadius: 1,
              px: 1.25, py: 0.6,
            }}>
              <WarningAmberIcon sx={{ color: '#e65100', fontSize: 16, flexShrink: 0, mt: '1px' }} />
              <Box>
                <Typography variant="caption" fontWeight={800} sx={{ color: '#e65100', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}>
                  Alergias
                </Typography>
                <Typography variant="body2" sx={{ color: '#bf360c', fontWeight: 600, lineHeight: 1.4 }}>
                  {patient.allergies}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Medicación actual */}
          {patient.current_medications && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={700} color="primary.main">Medicación: </Typography>
              <Typography variant="caption" color="text.secondary">{patient.current_medications}</Typography>
            </Box>
          )}
        </Box>

        {/* ── Problemas del paciente como filtros ── */}
        {problems.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
              Filtrar consultas por problema
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {problems.map(p => {
                const count = problemCounts[p.id] || 0;
                const isActive = activeProblem?.id === p.id;
                const DOT = { activo: 'error', cronico: 'warning', resuelto: 'default' };
                return (
                  <Chip
                    key={p.id}
                    label={`${p.name}${count > 0 ? ` (${count})` : ''}`}
                    size="small"
                    color={isActive ? (DOT[p.status] || 'primary') : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                    onClick={() => onProblemClick(isActive ? null : p)}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: isActive ? 700 : 400,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Antecedentes en texto libre (legado) — PatientProblem arriba es la fuente de verdad ── */}
        {patient.chronic_diseases && (
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', lineHeight: 1.4 }}>
              Antecedentes (legado): {patient.chronic_diseases}
            </Typography>
          </Box>
        )}

        {/* ── Tabs secundarios ── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={sideTab} onChange={(_, v) => setSideTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, fontSize: '0.78rem', px: 2 } }}>
            {sideTabs.map((l, i) => <Tab key={i} label={l} />)}
          </Tabs>
        </Box>

        {/* ── Contenido del tab lateral ── */}
        <Box sx={{ p: 2, overflow: 'auto', maxHeight: { md: 'calc(100vh - 280px)' }, minHeight: { md: 200 } }}>
          {sideTabs[sideTab] === 'Resumen' && (
            <ResumenTab
              records={records}
              patient={patient}
              onNewRecord={null}
              onGoToConsultas={null}
              onGoToEstudios={() => setSideTab(sideTabs.indexOf('Estudios'))}
              compact
              externalProblems={problems}
              onProblemsChanged={onUpdated}
            />
          )}
          {sideTabs[sideTab] === 'Turnos' && <AppointmentsTab patient={patient} doctors={doctors} />}
          {sideTabs[sideTab] === 'Estudios' && <EstudiosTab patient={patient} />}
          {sideTabs[sideTab] === 'Recetas' && <RecetasTab patient={patient} doctors={doctors} />}
          {sideTabs[sideTab] === 'Órdenes' && <OrdenesTab patient={patient} doctors={doctors} />}
          {sideTabs[sideTab] === 'Comunicaciones' && <ComunicacionesTab patient={patient} />}
        </Box>
      </Paper>

      <EditPatientDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        patient={patient}
        onUpdated={onUpdated}
      />

      <SelfUpdateLinkDialog
        open={selfUpdateOpen}
        onClose={() => setSelfUpdateOpen(false)}
        patientId={patient.id}
        patientPhone={patient.phone}
      />
    </>
  );
};
